(() => {
  const roots = document.querySelectorAll('[data-sleeping-pad-details]');

  const decodeText = (value) => {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = value || '';
    return decoder.value.replace(/\s+/g, ' ').trim();
  };

  const decimal = (value) => String(value || '').replace('.', ',');

  const numeric = (value) => {
    const parsed = Number.parseFloat(String(value || '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const firstMatch = (text, patterns) => {
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) return match;
    }
    return null;
  };

  const formatDimensions = (value) => {
    if (!value) return '';
    return value
      .replace(/\s*[x×]\s*/gi, ' × ')
      .replace(/\s*-\s*/g, '–')
      .replace(/\./g, ',')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const paragraph = (heading, text) => `<p><strong>${heading}</strong>${text}</p>`;

  const findRValue = (text) => {
    const match = text.match(/R[- ]?værdi\s*(?::|på)?\s*(?:hele\s*)?(\d+(?:[.,]\d+)?)/i);
    return match ? numeric(match[1]) : null;
  };

  const findThickness = (text) => {
    const match = firstMatch(text, [
      /Tykkelse\s+i\s+toppen\s*:\s*(\d+(?:[.,]\d+)?)\s*cm/i,
      /Tykkelse\s*(?::|på)?\s*(?:hele\s*)?(\d+(?:[.,]\d+)?)\s*cm/i,
      /(\d+(?:[.,]\d+)?)\s*cm\.?\s*tykkelse/i,
      /Udfoldede\s+mål\s*:\s*\d+(?:[.,]\d+)?\s*[x×]\s*\d+(?:[.,]\d+)?\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*cm/i,
    ]);
    return match ? numeric(match[1]) : null;
  };

  const findDimensions = (text) => {
    const withoutPackedDimensions = text.replace(
      /(?:Sammenpakket\s+størrelse|Sammenfoldet\s+størrelse|Sammenrullede\s+mål)\s*:\s*\d+(?:[.,]\d+)?\s*[x×]\s*\d+(?:[.,]\d+)?(?:\s*[x×]\s*\d+(?:[.,]\d+)?)?\s*cm/gi,
      ' '
    );
    const explicit = firstMatch(withoutPackedDimensions, [
      /(?:Udfoldede\s+mål|Størrelse)\s*:\s*(\d+(?:[.,]\d+)?\s*[x×]\s*\d+(?:[.,]\d+)?(?:\s*[x×]\s*\d+(?:[.,]\d+)?)?)\s*cm/i,
      /(^|[.!?]\s+)(\d+(?:[.,]\d+)?\s*[x×]\s*\d+(?:[.,]\d+)?(?:\s*[x×]\s*\d+(?:[.,]\d+)?)?)\s*cm/i,
    ]);

    if (explicit) {
      return formatDimensions(explicit[2] || explicit[1]);
    }

    const length = text.match(/Længde\s*:\s*(\d+(?:[.,]\d+)?)\s*cm/i);
    const width = text.match(/Bredde\s*:\s*(?:i\s+toppen\s*)?(\d+(?:[.,]\d+)?)(?:\s*-\s*(\d+(?:[.,]\d+)?))?\s*cm/i);
    if (length && width) {
      const widthLabel = width[2] ? `${decimal(width[1])}–${decimal(width[2])}` : decimal(width[1]);
      return `${decimal(length[1])} × ${widthLabel}`;
    }

    const fallback = withoutPackedDimensions.match(/(\d+(?:[.,]\d+)?\s*[x×]\s*\d+(?:[.,]\d+)?(?:\s*[x×]\s*\d+(?:[.,]\d+)?)?)\s*cm/i);
    return fallback ? formatDimensions(fallback[1]) : '';
  };

  const findWeight = (text) => {
    const preferred = text.match(/\bkun\s+(\d+(?:[.,]\d+)?)\s*(kg|g|gram)\b/i);
    const match = preferred || text.match(/Vægt\s*(?::|kun)?\s*(\d+(?:[.,]\d+)?)\s*(kg|g|gram)\b/i);
    if (!match) return null;

    const amount = numeric(match[1]);
    const unit = match[2].toLowerCase();
    if (amount === null) return null;

    return {
      grams: unit === 'kg' ? amount * 1000 : amount,
      label: unit === 'kg' ? `${decimal(match[1])} kg` : `${decimal(match[1])} g`,
    };
  };

  const findPackedDimensions = (text) => {
    const match = text.match(/(?:Sammenpakket\s+størrelse|Sammenfoldet\s+størrelse|Sammenrullede\s+mål)\s*:\s*(\d+(?:[.,]\d+)?\s*[x×]\s*\d+(?:[.,]\d+)?(?:\s*[x×]\s*\d+(?:[.,]\d+)?)?)\s*cm/i);
    return match ? formatDimensions(match[1]) : '';
  };

  const findInflationTime = (text) => {
    const match = text.match(/(?:Puster|Pustes|Pustet|Puster\s+sig)\s+(?:sig\s+)?(?:selv\s+)?op\s+(?:på|med\s+munden\s+på)?\s*(?:ca\.?\s*)?(\d+(?:[.,]\d+)?)\s*min/i);
    return match ? decimal(match[1]) : '';
  };

  const buildRValue = (value, isReflective) => {
    if (value === null) {
      return {
        value: 'Ikke oplyst',
        body:
          paragraph(
            'R-værdien er ikke oplyst.',
            isReflective
              ? ' Dette er et tyndt, reflekterende underlag, som er beregnet til at supplere et liggeunderlag eller en luftmadras – ikke til at erstatte isoleringen i et egentligt liggeunderlag.'
              : ' Producenten har ikke angivet en testet R-værdi, så vi vil ikke gætte på isoleringsevnen.'
          ) +
          paragraph(
            'Sådan læser du R-værdien.',
            ' Jo højere tallet er, desto bedre beskytter underlaget mod kulde fra jorden. Ved kølige temperaturer bør det bruges sammen med et liggeunderlag med en dokumenteret R-værdi.'
          ),
      };
    }

    let level = 'Let isolering';
    let seasonCopy = 'Det passer bedst til lune nætter, festival og sommerbrug, hvor jorden ikke er kold.';
    if (value >= 6) {
      level = 'Meget høj isolering';
      seasonCopy = 'Det giver meget effektiv beskyttelse mod kulde fra jorden og er velegnet, når komfort og helårsbrug prioriteres.';
    } else if (value >= 3) {
      level = 'God isolering';
      seasonCopy = 'Det er et alsidigt niveau til størstedelen af den danske campingsæson, også når nætterne bliver kølige.';
    }

    return {
      value: `R ${decimal(value)}`,
      body:
        paragraph(`${level}.`, ` Liggeunderlaget har en R-værdi på ${decimal(value)}. ${seasonCopy}`) +
        paragraph(
          'R-værdien beskriver isoleringen.',
          ' Den fortæller, hvor godt underlaget bremser kulde fra jorden. Den siger ikke noget om blødhed – det gør tykkelsen og konstruktionen.'
        ),
    };
  };

  const buildThickness = (value, text, title) => {
    const isReflective = /isolerende\s+underlag/i.test(title) && value !== null && value < 1;
    if (value === null) {
      return {
        value: 'Ikke oplyst',
        body: paragraph('Tykkelsen er ikke oplyst.', ' Se produktbillederne og beskrivelsen for at vurdere konstruktion og komfort.'),
      };
    }

    let lead = 'Kompakt komfort.';
    let copy = ' Den lave profil holder pakkestørrelsen nede og passer bedst, når lav vægt og enkel transport er vigtigere end sengelignende komfort.';
    if (isReflective) {
      lead = 'Tyndt supplement på 0,2 cm.';
      copy = ' Underlaget skaber ikke blød liggekomfort alene. Det bruges under et andet underlag eller en luftmadras for at reflektere kropsvarme og beskytte mod fugt fra jorden.';
    } else if (value >= 10) {
      lead = `${decimal(value)} cm med høj komfort.`;
      copy = ' Den ekstra højde giver god afstand til jorden og en mere sengelignende følelse. Det er især behageligt for sidesovere og på længere campingture.';
    } else if (value >= 5) {
      lead = `${decimal(value)} cm balanceret komfort.`;
      copy = ' Tykkelsen giver en god kombination af støtte og liggekomfort uden at gøre underlaget unødigt stort.';
    }

    let extra = '';
    if (/memoryfoam|memoryfoam|pongee/i.test(text)) {
      extra = paragraph('Blød kerne med støtte.', ' Skumkonstruktionen former sig efter kroppen og hjælper med at fordele trykket, så hofter og skuldre ligger mere behageligt.');
    } else if (/integreret\s+pude|indbygget\s+pude/i.test(text)) {
      extra = paragraph('Puden er integreret.', ' Hovedenden er hævet, så du får støtte uden nødvendigvis at skulle pakke en separat pude.');
    }

    return { value: `${decimal(value)} cm`, body: paragraph(lead, copy) + extra };
  };

  const buildSize = (dimensions, text, title) => {
    if (!dimensions) {
      return {
        value: 'Ikke oplyst',
        body: paragraph('De udfoldede mål er ikke oplyst.', ' Se produktbeskrivelsen for information om pasform og antal sovepladser.'),
      };
    }

    const isCar = /til\s+(?:bil|tesla)/i.test(title);
    const isDouble = /dobbelt|2\s+personer/i.test(`${title} ${text}`) || /×\s*(?:1[0-9]{2}|9[4-9])(?:–\d+)?$/i.test(dimensions);
    let body;
    if (isCar) {
      body =
        paragraph('Formet til bilens bagagerum.', ` De udfoldede mål er ${dimensions} cm. Den varierende bredde følger kabinens form og giver en mere sammenhængende soveflade.`) +
        paragraph('Kontrollér pladsen i din bil.', ' Mål længde og bredde med bagsæderne lagt ned, da kabinens udformning kan variere mellem årgange og modeller.');
    } else if (isDouble) {
      body =
        paragraph('Plads til to personer.', ` De udfoldede mål er ${dimensions} cm. Den brede liggeflade giver jer én samlet soveplads uden en åbning mellem to underlag.`) +
        paragraph('Sammenlign med teltets bundmål.', ' Der bør stadig være lidt fri plads omkring underlaget til tasker, sko og teltets skrå sider.');
    } else {
      body =
        paragraph('Liggeflade til én person.', ` De udfoldede mål er ${dimensions} cm. Det gør det nemt at sammenligne længde og bredde med din kropshøjde og dit telt.`) +
        paragraph('Vælg efter pladsbehov.', ' Et bredere underlag giver mere bevægelsesfrihed, mens en smallere model er lettere at få plads til i et kompakt telt.');
    }

    return { value: `${dimensions} cm`, body };
  };

  const buildWeight = (weight, title) => {
    if (!weight) {
      return {
        value: 'Ikke oplyst',
        body: paragraph('Vægten er ikke oplyst.', ' Hvis lav oppakningsvægt er afgørende, anbefaler vi at få den præcise vægt bekræftet før køb.'),
      };
    }

    let lead = 'God balance mellem komfort og transport.';
    let copy = ' Vægten passer godt til almindelig camping og ture, hvor underlaget ikke skal bæres langt.';
    if (weight.grams <= 1000) {
      lead = 'Let at have med.';
      copy = ' Den lave vægt gør underlaget velegnet til vandring, cykelture og andre ture, hvor hvert gram i oppakningen betyder noget.';
    } else if (weight.grams >= 4000) {
      lead = 'Komfort frem for lav vægt.';
      copy = /bil/i.test(title)
        ? ' Underlaget er primært tænkt til bilferie og overnatning i bilen, hvor den højere vægt har mindre betydning end komforten.'
        : ' Den solide konstruktion giver høj komfort, men passer bedst til camping og ture, hvor underlaget transporteres i bil.';
    }

    return {
      value: weight.label,
      body: paragraph(lead, copy) + paragraph('Husk hele oppakningen.', ' Den samlede transportvægt inkluderer også pose, pumpe og eventuelt andet tilbehør.'),
    };
  };

  const buildInflation = (text, title, time) => {
    const lower = `${title} ${text}`.toLowerCase();
    const timeLabel = time ? ` · ${time} min.` : '';
    const noInflation = /isolerende\s+underlag/.test(lower);
    const electric = /elektrisk\s+pumpe|indbygget\s+pumpe.*usb|usb\s*c/.test(lower);
    const footPump = /fodpumpe/.test(lower);
    const pumpBag = /pump\s*(?:bag|sack)|pumpepose/.test(lower);
    const selfInflating = /selvoppustelig|puster\s+sig\s+selv/.test(lower);
    const mouth = /med\s+munden/.test(lower);

    if (noInflation) {
      return {
        value: 'Klar med det samme',
        body:
          paragraph('Kræver ingen oppustning.', ' Rul underlaget ud, og placér det med den reflekterende side mod kroppen.') +
          paragraph('Bruges som supplement.', ' Det fungerer bedst under et liggeunderlag, en luftmadras eller en sovepose og kan rulles sammen igen efter brug.'),
      };
    }

    if (electric) {
      return {
        value: `Selvoppustelig${timeLabel}`,
        body:
          paragraph('Lad ventilerne gøre arbejdet.', ` Åbn ventilerne, og lad skummet trække luft ind${time ? ` i cirka ${time} minutter` : ''}. Justér til sidst fastheden efter behov.`) +
          paragraph('Elektrisk hjælp til tømning.', ' Den indbyggede, genopladelige USB-C-pumpe suger luften ud igen, så underlaget er lettere at få tilbage i transportposen.'),
      };
    }

    if (footPump) {
      return {
        value: `Indbygget fodpumpe${timeLabel}`,
        body:
          paragraph('Pumpes op uden ekstra udstyr.', ` Den indbyggede fodpumpe fylder underlaget med luft${time ? ` på cirka ${time} minutter` : ''}, så du undgår at puste med munden.`) +
          paragraph('Nem justering af fastheden.', ' Stop, når underlaget føles behageligt. En anelse eftergivenhed giver ofte bedre støtte end et helt hårdt underlag.'),
      };
    }

    if (pumpBag) {
      return {
        value: `Pumpepose${timeLabel}`,
        body:
          paragraph('Pumpes op med den medfølgende pose.', ` Pumpeposen fyldes med luft og presses ind i underlaget${time ? `, som typisk er klar på cirka ${time} minutter` : ''}.`) +
          paragraph('Mindre fugt i underlaget.', ' Metoden reducerer behovet for at puste med munden og begrænser derfor fugt fra udåndingsluft i kamrene.'),
      };
    }

    if (selfInflating) {
      return {
        value: `Selvoppustelig${timeLabel}`,
        body:
          paragraph('Åbn ventilen, og vent.', ` Skummet udvider sig og trækker automatisk luft ind${time ? ` på cirka ${time} minutter` : ''}. Første oppustning kan tage lidt længere efter opbevaring.`) +
          paragraph('Tilpas fastheden.', ' Giv eventuelt et par pust til sidst, hvis du ønsker en fastere liggeflade, og luk derefter ventilen helt.'),
      };
    }

    if (mouth) {
      return {
        value: `Med munden${timeLabel}`,
        body:
          paragraph('Hurtig manuel oppustning.', ` Underlaget pustes op med munden${time ? ` på cirka ${time} minutter` : ''} og kræver derfor ingen separat pumpe.`) +
          paragraph('Justér fastheden til sidst.', ' Luk ventilen, når underlaget støtter kroppen uden at føles unødigt hårdt.'),
      };
    }

    return {
      value: time ? `Ca. ${time} min.` : 'Enkel oppustning',
      body:
        paragraph('Nem at gøre klar.', time ? ` Produktbeskrivelsen angiver en oppustningstid på cirka ${time} minutter.` : ' Følg produktets ventil- og pumpeanvisning for den hurtigste oppustning.') +
        paragraph('Tilpas fastheden.', ' Et underlag skal støtte kroppen, men behøver ikke være pustet helt hårdt for at være behageligt.'),
    };
  };

  const buildPacked = (dimensions, title) => {
    if (!dimensions) {
      return {
        value: 'Ikke oplyst',
        body: paragraph('Transportmålet er ikke oplyst.', ' Brug produktets vægt og type som pejlemærke, eller kontakt kundeservice, hvis målet er afgørende for din oppakning.'),
      };
    }

    const values = dimensions.match(/\d+(?:[.,]\d+)?/g) || [];
    const largest = Math.max(...values.map(numeric).filter((value) => value !== null));
    const compact = largest <= 37;
    const car = /bil|dobbelt|deluxe/i.test(title);
    let lead = compact ? 'Kompakt i oppakningen.' : 'Transportmål til planlægning.';
    let copy = compact
      ? ' Det lille sammenpakkede format gør underlaget nemt at placere i eller uden på rygsækken.'
      : car
        ? ' Underlaget fylder mere sammenpakket, fordi det prioriterer liggekomfort og en større soveflade. Det passer derfor bedst til transport i bil.'
        : ' Sammenlign målene med pladsen i bagage eller oppakning, så du ved, hvor transportposen kan ligge.';

    return {
      value: `${dimensions} cm`,
      body: paragraph(lead, ` Sammenpakket måler underlaget ${dimensions} cm.${copy}`) + paragraph('Pak det tørt.', ' Lad underlaget tørre efter brug, og opbevar det efter producentens anvisning for at passe bedst muligt på materialer og ventiler.'),
    };
  };

  const render = (root) => {
    const dataElement = root.querySelector('[data-sleeping-pad-product-data]');
    if (!dataElement) return;

    let product;
    try {
      product = JSON.parse(dataElement.textContent);
    } catch (error) {
      return;
    }

    const title = decodeText(product.title);
    const text = decodeText(product.description);
    if (!title || !text) return;

    const rValue = findRValue(text);
    const thickness = findThickness(text);
    const dimensions = findDimensions(text);
    const weight = findWeight(text);
    const packed = findPackedDimensions(text);
    const inflationTime = findInflationTime(text);
    const isReflective = /isolerende\s+underlag/i.test(title);

    const details = {
      'r-value': buildRValue(rValue, isReflective),
      thickness: buildThickness(thickness, text, title),
      size: buildSize(dimensions, text, title),
      weight: buildWeight(weight, title),
      inflation: buildInflation(text, title, inflationTime),
      packed: buildPacked(packed, title),
    };

    Object.entries(details).forEach(([key, detail]) => {
      const card = root.querySelector(`[data-pad-detail="${key}"]`);
      if (!card) return;
      const value = card.querySelector('[data-pad-value]');
      const body = card.querySelector('[data-pad-body]');
      if (value) value.textContent = detail.value;
      if (body) body.innerHTML = detail.body;
    });

    root.querySelectorAll('details').forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        root.querySelectorAll('details[open]').forEach((openItem) => {
          if (openItem !== item) openItem.open = false;
        });
      });
    });

    root.hidden = false;
  };

  roots.forEach(render);
})();
